import { StrictMode, useCallback, useMemo } from 'react'
import type { VFC } from 'react'
import {
  canonicalIANATimezoneIdentifier,
  timeZoneData,
  timeZoneName,
  utcOffsetForZoneAsString,
} from '@opallabs/concepts'
import type { IANATimezoneIdentifier } from '@opallabs/concepts'
import { DeprecatedSingleSelect } from '@opallabs/lapidary'
import type { TimeZoneOption, TimeZoneSelectProps } from './types'

export { type TimeZoneSelectProps }

type BuildOptionsProps = Readonly<
  Pick<TimeZoneSelectProps, 'date' | 'locale'> & {
    withRegionName?: boolean
  }
>
type BuildOptionProps = Readonly<
  Required<Omit<BuildOptionsProps, 'withRegionName'>> & {
    examples: string
    name: IANATimezoneIdentifier
  }
>
const buildOption = ({
  date,
  examples,
  locale,
  name,
}: BuildOptionProps): TimeZoneOption => {
  if (name === 'Etc/UTC') {
    return {
      label: examples,
      value: name,
    } as const
  }

  const { short } = timeZoneName(date, name, { locale })
  const niceOffset = utcOffsetForZoneAsString(date, name, {
    includeGMT: true,
    locale,
  })
  const summary = short ? `${short} (${niceOffset})` : niceOffset

  return {
    label: `${summary} • ${examples}`,
    value: name,
  } as const
}

/**
 * @private This is exported for the `<TimeZoneSelect>` in Photon, which is a
 *   deprecated component that will be replaced by the `<TimeZoneSelect>` in
 *   this file. Do not use.
 * @private This is exported for use in tests.
 * @deprecated Use `<TimeZoneSelect>` directly.
 */
export const buildOptions = ({
  date,
  locale = navigator.language,
  withRegionName = false,
}: BuildOptionsProps): TimeZoneOption[] => {
  const rawData = timeZoneData({ locale, withRegionName })
  const options: TimeZoneOption[] = []

  rawData.forEach((examples, name) => {
    const option = buildOption({ date, examples, locale, name })
    options.push(option)
  })

  return options
}

export const TimeZoneSelect: VFC<TimeZoneSelectProps> = ({
  date,
  disabled,
  locale,
  onChange,
  selectedZone,
  styleOverrides,
}) => {
  const options = useMemo(() => {
    return buildOptions({ date, locale })
  }, [date, locale])
  const initialSelection = useMemo(() => {
    if (!selectedZone) return undefined

    const resolvedZone = canonicalIANATimezoneIdentifier(selectedZone)
    return options.find(({ value }) => value === resolvedZone)
  }, [selectedZone, options])
  const wrappedOnChange = useCallback(
    (selectedItem: TimeZoneOption | null) => {
      if (!onChange || !selectedItem) return

      onChange(selectedItem.value)
    },
    [onChange]
  )

  return (
    <StrictMode>
      <DeprecatedSingleSelect<TimeZoneOption>
        css={styleOverrides}
        onChange={wrappedOnChange}
        placeholder="Select time zone…"
        isSearchable
        {...{ disabled, initialSelection, options }}
      />
    </StrictMode>
  )
}
TimeZoneSelect.displayName = 'Satellite: TimeZoneSelect'
